import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { IS_DESKTOP_EXPERIENCE } from '../lib/deviceGate'
import { usePhase1Progress, usePhase1Status } from '../lib/hooks/useSceneReadiness'
import {
  advance,
  getOpeningStep,
  prefersReducedOpeningMotion,
  subscribeOpening,
} from '../lib/openingSequence'
import { MOTION } from '../lib/motion'

const FADE_MS = MOTION.ms.fade
const FADE_FALLBACK_MS = FADE_MS + 150

const STATUS_LINE = {
  scene: 'loading scene.',
  characters: 'loading characters.',
  decoding: 'almost done.',
  done: 'done.',
}

const WelcomeScreen = ({ variant = 'desktop' }) => {
  const isFlat = variant === 'flat'
  const progress = usePhase1Progress()
  const status = usePhase1Status()
  const step = useSyncExternalStore(subscribeOpening, getOpeningStep, getOpeningStep)
  const [mounted, setMounted] = useState(() => {
    if (step === 'done') return false
    return isFlat || IS_DESKTOP_EXPERIENCE
  })
  const fadingRef = useRef(false)
  const reducedMotion = prefersReducedOpeningMotion()

  useLayoutEffect(() => {
    document.getElementById('welcome-static')?.remove()
  }, [])

  useEffect(() => {
    if (!isFlat && !IS_DESKTOP_EXPERIENCE) return undefined
    if (step === 'done' || step === 'chrome' || step === 'characters') {
      setMounted(false)
      return undefined
    }
    if (step === 'welcome' || step === 'fading') {
      setMounted(true)
    }
    return undefined
  }, [isFlat, step])

  useEffect(() => {
    if (step !== 'fading' || fadingRef.current) return undefined
    fadingRef.current = true

    if (reducedMotion) {
      advance('fading')
      return undefined
    }

    const node = document.querySelector('.welcome-screen')
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      advance('fading')
    }

    const onTransitionEnd = (event) => {
      if (event.target !== node) return
      if (event.propertyName && event.propertyName !== 'opacity') return
      finish()
    }

    node?.addEventListener('transitionend', onTransitionEnd)
    const timer = window.setTimeout(finish, FADE_FALLBACK_MS)
    return () => {
      node?.removeEventListener('transitionend', onTransitionEnd)
      window.clearTimeout(timer)
    }
  }, [step, reducedMotion])

  if (!mounted) return null
  if (!isFlat && !IS_DESKTOP_EXPERIENCE) return null

  const isFading = step === 'fading'
  const percent = Math.round(Math.min(1, Math.max(0, progress)) * 100)
  const loadingLine = STATUS_LINE[status] || STATUS_LINE.scene

  return (
    <div
      className={isFading ? 'welcome-screen is-fading' : 'welcome-screen'}
      role={isFlat ? undefined : 'progressbar'}
      aria-valuemin={isFlat ? undefined : 0}
      aria-valuemax={isFlat ? undefined : 100}
      aria-valuenow={isFlat ? undefined : percent}
      aria-label={isFlat ? 'welcome.' : loadingLine}
    >
      <div className="welcome-screen-inner">
        <p className="welcome-title">welcome.</p>
        {isFlat ? null : (
          <>
            <div className="welcome-bar-track">
              <div
                className="welcome-bar-fill"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="welcome-status">{loadingLine}</p>
          </>
        )}
      </div>
    </div>
  )
}

export default WelcomeScreen
